import clsx from "clsx";
import {
  REVIEW_WORKFLOW_STEP_ORDER,
  type ReviewWorkflowStep,
} from "@/components/case-checking/CaseReviewWorkflow";
import { TEXT_MUTED, TEXT_ROW_SUBTITLE } from "@/components/ui/surface/surfaceTokens";
import type { GuidedReviewStep, ProgressComparison } from "@/services/api/case-checking";

const SHORT_STEP_LABEL: Record<ReviewWorkflowStep, string> = {
  narrative: "Narrative",
  documents: "Docs",
  checklist: "Complete",
  rules: "Rules",
  assessment: "Assess",
  actions: "Actions",
  summary: "Summary",
};

function stepCompleted(completed: GuidedReviewStep[], step: GuidedReviewStep): boolean {
  return completed.includes(step);
}

export function ReviewProgressStrip({
  completed,
  className,
}: {
  completed: GuidedReviewStep[];
  className?: string;
}) {
  const completedCount = REVIEW_WORKFLOW_STEP_ORDER.filter((s) => stepCompleted(completed, s)).length;
  const ariaLabel = `${completedCount} of ${REVIEW_WORKFLOW_STEP_ORDER.length} steps complete`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={clsx("flex w-[13rem] max-w-full gap-0.5", className)}
      data-testid="review-progress-strip"
    >
      {REVIEW_WORKFLOW_STEP_ORDER.map((s) => {
        const done = stepCompleted(completed, s);
        return (
          <span
            key={s}
            className={clsx(
              "h-1.5 min-w-0 flex-1 rounded-full",
              done ? "bg-indigo-500 dark:bg-indigo-400" : "bg-slate-200 dark:bg-slate-700/80",
            )}
            data-testid={`progress-strip-segment-${s}`}
            data-completed={done ? "true" : "false"}
          />
        );
      })}
    </div>
  );
}

const COMPARE_GRID = "grid grid-cols-[5.5rem_repeat(7,minmax(2.75rem,1fr))] items-center gap-x-1 gap-y-1.5";

function ProgressCompareChips({
  rowKey,
  completed,
}: {
  rowKey: "selected" | "current";
  completed: GuidedReviewStep[];
}) {
  return REVIEW_WORKFLOW_STEP_ORDER.map((step) => {
    const done = stepCompleted(completed, step);
    return (
      <span
        key={`${rowKey}-${step}`}
        className={clsx(
          "flex h-5 items-center justify-center rounded-md text-[10px] font-medium",
          done
            ? "bg-indigo-500/15 text-indigo-800 dark:text-indigo-200"
            : "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-500",
        )}
        data-testid={`progress-compare-chip-${rowKey}-${step}`}
        data-completed={done ? "true" : "false"}
      >
        {done ? "✓" : ""}
      </span>
    );
  });
}

export function ReviewProgressCompare({ progress }: { progress: ProgressComparison }) {
  return (
    <div className="max-w-[40rem] overflow-x-auto" data-testid="progress-compare">
      <div className={COMPARE_GRID}>
        <span />
        {REVIEW_WORKFLOW_STEP_ORDER.map((step) => (
          <p
            key={step}
            className={clsx(TEXT_MUTED, "truncate rounded-md py-0.5 text-center text-[10px]")}
            data-testid={`progress-col-${step}`}
          >
            {SHORT_STEP_LABEL[step]}
          </p>
        ))}
        <span className={clsx(TEXT_ROW_SUBTITLE, "font-medium")}>This review</span>
        <ProgressCompareChips rowKey="selected" completed={progress.selected_completed} />
        <span className={clsx(TEXT_ROW_SUBTITLE, "font-medium")}>Current</span>
        <ProgressCompareChips rowKey="current" completed={progress.current_completed} />
      </div>
    </div>
  );
}
